import type { Permission } from "../../../../../types/types";
import {
  AttributeInput,
  AttributeSchema,
  AttributeValue,
  CollectionSchema,
  PIMProduct,
  SupportedAssetAccessibility,
} from "../../../../../types/types.PIM";
import { shouldShowObjectReferenceAttribute } from "../../../../../components/ObjectReference/utils";

// Intentionally duplicated to avoid importing product_overview.utils
export const getProductTypePermissionSuffix = (pim_product_type: string) => {
  switch (pim_product_type) {
    case "master":
      return "product_type_master";
    case "variant":
      return "product_type_variant";
    case "standard":
    default:
      return "product_type_standard";
  }
};

export const ENTITY_PERMISSIONS: Record<
  string,
  { view: Permission; modify: Permission; publish: Permission }
> = {
  product: {
    view: "view_products",
    modify: "modify_products",
    publish: "publish_products",
  },
  supplier: {
    view: "view_suppliers",
    modify: "modify_suppliers",
    publish: "publish_suppliers",
  },
  customer: {
    view: "view_customers",
    modify: "modify_customers",
    publish: "publish_customers",
  },
  distributor: {
    view: "view_distributors",
    modify: "modify_distributors",
    publish: "publish_distributors",
  },
};

export function getReferencePermissionSet(
  objectType: string,
  pimProductType?: PIMProduct["pim_product_type"] | null
): { view: Permission; modify: Permission; publish: Permission } | undefined {
  if (objectType === "product" && pimProductType) {
    const suffix = getProductTypePermissionSuffix(pimProductType);

    if (suffix) {
      return {
        view: `view_${suffix}` as Permission,
        modify: `modify_${suffix}` as Permission,
        publish: `publish_${suffix}` as Permission,
      };
    }
  }

  return ENTITY_PERMISSIONS[objectType]; // handles product, supplier, customer, distributor
}

export function collectionAttributeValueHasDisplayValue(
  attrValue: AttributeValue,
  inputType?: AttributeInput
): boolean {
  if (inputType === "document" || attrValue.data_type === "document") {
    return !!(attrValue.value as { asset_id?: string } | null)?.asset_id;
  }

  if (inputType === "object_reference") {
    return !!attrValue.object_ref?.id;
  }

  if (inputType === "product_reference" || attrValue.product_ref) {
    return !!attrValue.product_ref?.id;
  }

  if (attrValue.user) {
    return !!attrValue.user.id;
  }

  if (attrValue.enum_variant?.variant) {
    return true;
  }

  const { value } = attrValue;
  if (typeof value === "boolean" || typeof value === "number") {
    return true;
  }

  return value != null && value !== "";
}

export function collectionHasDisplayableValues(
  collection: CollectionSchema,
  objectReferenceEnabled: boolean,
  canModify = false
): boolean {
  const visibleColumns = collection.columns.filter(
    (column) =>
      column.is_visible &&
      shouldShowObjectReferenceAttribute({
        inputType: column.input_type,
        objectReferenceEnabled,
      })
  );

  if (!visibleColumns.length) {
    return false;
  }

  const columnById = new Map(
    visibleColumns.map((column) => [column.id, column])
  );

  const hasDisplayableRows = (collection.value_rows ?? []).some((row) =>
    row.values.some((attrValue) => {
      const column = columnById.get(attrValue.attribute_id);
      if (!column) {
        return false;
      }
      return collectionAttributeValueHasDisplayValue(
        attrValue,
        column.input_type
      );
    })
  );

  return hasDisplayableRows || canModify;
}

export const isAttributeVisibleInCard = (
  attribute: AttributeSchema,
  roleIsGuest = false,
  roleIsSomeKindOfBuyer = false
): boolean => {
  if (!attribute.is_visible) return false;

  const is_not_accessible = (accessibility?: SupportedAssetAccessibility) => {
    return roleIsGuest
      ? accessibility !== "public"
      : roleIsSomeKindOfBuyer
      ? accessibility === "internal"
      : false;
  };

  const hasDocumentValues = attribute.values?.some(
    (entry) => (entry.value as { asset_id?: string } | null)?.asset_id
  );

  const hasObjectReferenceValues =
    attribute.input_type === "object_reference" &&
    attribute.values?.some((entry) => entry.object_ref?.id);

  if (
    attribute.input_type === "object_reference" &&
    !hasObjectReferenceValues
  ) {
    return false;
  }

  if (
    attribute.input_type !== "document" &&
    attribute.input_type !== "toggle" &&
    attribute.input_type !== "object_reference" &&
    !attribute.values?.[0]?.value
  ) {
    return false;
  }

  if (attribute.input_type === "document" && !hasDocumentValues) return false;

  if (
    (attribute.input_type === "image" || attribute.input_type === "document") &&
    is_not_accessible(attribute.values?.[0]?.visibility ?? "public")
  ) {
    return false;
  }

  if (attribute.is_not_applicable) return false;
  if (attribute.is_restricted && roleIsGuest) return false;

  return true;
};
